/**
 * Centralized TanStack Query key factory (APP-001 §11).
 *
 * Keys are structured hierarchically so invalidation can be scoped (e.g.
 * invalidate all `apiKeys` queries, or a single account bootstrap). All query
 * hooks use these factories — never inline string keys.
 *
 * Response-type relationships are documented at the call site (the dashboard
 * hooks in src/features/**), not encoded into the key type, to keep the key
 * tuples plain and JSON-serializable.
 */
export const queryKeys = {
  account: {
    all: ['account'] as const,
    identity: () => [...queryKeys.account.all, 'identity'] as const,
    /**
     * Full account response (POST-rename shape). Kept separate from
     * `identity` (the bootstrap DashboardIdentityResponse) because the rename
     * endpoint returns AccountResponse; callers merge the new name back into
     * the identity cache (APP-002 §29).
     */
    detail: () => [...queryKeys.account.all, 'detail'] as const,
  },
  overview: {
    all: ['overview'] as const,
    detail: () => [...queryKeys.overview.all, 'detail'] as const,
  },
  usage: {
    all: ['usage'] as const,
    summary: () => [...queryKeys.usage.all, 'summary'] as const,
    history: (params?: { granularity?: string; from?: string; to?: string; limit?: number; offset?: number }) =>
      [...queryKeys.usage.all, 'history', params ?? {}] as const,
  },
  apiKeys: {
    all: ['api-keys'] as const,
    list: (params?: { limit?: number; offset?: number }) =>
      [...queryKeys.apiKeys.all, 'list', params ?? {}] as const,
  },
  billing: {
    all: ['billing'] as const,
    summary: () => [...queryKeys.billing.all, 'summary'] as const,
  },
  bulk: {
    all: ['bulk'] as const,
    jobs: (params?: { limit?: number; offset?: number }) =>
      [...queryKeys.bulk.all, 'jobs', params ?? {}] as const,
    /** Single-job status (APP-007). Used by the polling hook. */
    detail: (jobId: string) => [...queryKeys.bulk.all, 'detail', jobId] as const,
  },
  webhooks: {
    all: ['webhooks'] as const,
    list: (params?: { limit?: number; offset?: number }) =>
      [...queryKeys.webhooks.all, 'list', params ?? {}] as const,
    /** Single-webhook config (APP-008). */
    detail: (webhookId: string) => [...queryKeys.webhooks.all, 'detail', webhookId] as const,
    /** Delivery history for a webhook (APP-008), optionally filtered by status. */
    deliveries: (webhookId: string, params?: { status?: string }) =>
      [...queryKeys.webhooks.all, 'deliveries', webhookId, params ?? {}] as const,
  },
} as const;

export type QueryKeyFactory = typeof queryKeys;
